import _ from 'lodash';

import {
  AI_PROCESS_RUNS_ACTION_TYPE,
  AiProcessStepTerminalStatuses,
  LoadStatus,
} from 'constants/aiProcessRuns';

import {
  appendMessageToStep,
  ensureFilters,
  mergeFiltersForOwner,
  mergeFormFieldEvent,
  sortStepIdsByStepNumber,
  toRecord,
  upsertExecution,
  upsertStep,
} from './aiProcessRuns.reducer.utils';

const initialState = {
  runsByOwner: {},
  runsById: {},
  stepsByRunId: {},
  stepsById: {},
  messagesByStepId: {},
  messagesById: {},
  citationsByStepId: {},
  citationsByExecutionId: {},
  blobsByStepId: {},
  pendingToolCallByStepId: {},
  pendingQuestionByStepId: {},
  processesByOwner: {},
  filtersByOwner: {},
  selectedExecutionIdByOwner: {},
  confirmingStepIds: {},
  answeringStepIds: {},
  retryingStepIds: {},
  retryingFromHereStepIds: {},
  formByExecutionId: {},
  formLoadStatusByExecutionId: {},
  formActionInFlightById: {},
  guidelineFullView: null,
  guidelineCache: {},
  isLoadingGuideline: false,
};

const handleLoadRuns = (state, { ownerId }) => ({
  ...state,
  runsByOwner: {
    ...state.runsByOwner,
    [ownerId]: {
      ids: state.runsByOwner[ownerId]?.ids ?? [],
      totalCount: state.runsByOwner[ownerId]?.totalCount ?? 0,
      status: LoadStatus.LOADING,
    },
  },
});

const handleLoadRunsSuccess = (state, { ownerId, items, totalCount }) => {
  const ids = items.map(item => item.id);
  const nextRunsById = items.reduce((acc, run) => ({ ...acc, [run.id]: run }), state.runsById);

  return {
    ...state,
    runsByOwner: {
      ...state.runsByOwner,
      [ownerId]: { ids, totalCount, status: LoadStatus.READY },
    },
    runsById: nextRunsById,
  };
};

const handleLoadRunsFailure = (state, { ownerId }) => ({
  ...state,
  runsByOwner: {
    ...state.runsByOwner,
    [ownerId]: {
      ids: state.runsByOwner[ownerId]?.ids ?? [],
      totalCount: state.runsByOwner[ownerId]?.totalCount ?? 0,
      status: LoadStatus.ERROR,
    },
  },
});

const handleSetRunsFilter = (state, { ownerId, partial }) => {
  const currentFilters = state.filtersByOwner[ownerId];
  const nextFilters = mergeFiltersForOwner(currentFilters, partial);

  if (_.isEqual(ensureFilters(currentFilters), nextFilters)) {
    return state;
  }

  return {
    ...state,
    filtersByOwner: {
      ...state.filtersByOwner,
      [ownerId]: nextFilters,
    },
  };
};

const handleSetRunsPage = (state, { ownerId, skip }) => ({
  ...state,
  filtersByOwner: {
    ...state.filtersByOwner,
    [ownerId]: {
      ...ensureFilters(state.filtersByOwner[ownerId]),
      skip,
    },
  },
});

const handleLoadProcessesUsed = (state, { ownerId }) => ({
  ...state,
  processesByOwner: {
    ...state.processesByOwner,
    [ownerId]: {
      items: state.processesByOwner[ownerId]?.items ?? [],
      status: LoadStatus.LOADING,
    },
  },
});

const handleLoadProcessesUsedSuccess = (state, { ownerId, items }) => ({
  ...state,
  processesByOwner: {
    ...state.processesByOwner,
    [ownerId]: { items, status: LoadStatus.READY },
  },
});

const handleLoadProcessesUsedFailure = (state, { ownerId }) => ({
  ...state,
  processesByOwner: {
    ...state.processesByOwner,
    [ownerId]: {
      items: state.processesByOwner[ownerId]?.items ?? [],
      status: LoadStatus.ERROR,
    },
  },
});

const handleLoadRunDetail = (state, { executionId }) => ({
  ...state,
  stepsByRunId: {
    ...state.stepsByRunId,
    [executionId]: {
      ids: state.stepsByRunId[executionId]?.ids ?? [],
      status: LoadStatus.LOADING,
    },
  },
});

const handleLoadRunDetailSuccess = (state, { executionId, run, steps }) => {
  const stepsRecord = toRecord(steps);
  const stepIds = sortStepIdsByStepNumber(
    steps.map(step => step.id),
    stepsRecord,
  );

  return {
    ...state,
    runsById: { ...state.runsById, ...(run ? { [run.id]: run } : {}) },
    stepsByRunId: {
      ...state.stepsByRunId,
      [executionId]: { ids: stepIds, status: LoadStatus.READY },
    },
    stepsById: { ...state.stepsById, ...stepsRecord },
  };
};

const handleLoadRunDetailFailure = (state, { executionId }) => ({
  ...state,
  stepsByRunId: {
    ...state.stepsByRunId,
    [executionId]: {
      ids: state.stepsByRunId[executionId]?.ids ?? [],
      status: LoadStatus.ERROR,
    },
  },
});

const handleLoadStepMessages = (state, { stepExecutionId }) => ({
  ...state,
  messagesByStepId: {
    ...state.messagesByStepId,
    [stepExecutionId]: {
      ids: state.messagesByStepId[stepExecutionId]?.ids ?? [],
      status: LoadStatus.LOADING,
    },
  },
});

const handleLoadStepMessagesSuccess = (state, { stepExecutionId, messages }) => {
  const serverIds = messages.map(message => message.id);
  const messagesRecord = toRecord(messages);

  // LEN-26: a running step persists its transcript only in the step-end batch,
  // so this snapshot load returns empty (or a partial prefix) mid-run. Replacing
  // the id list wholesale would wipe the live step_message_appended rows that
  // arrived while the load was in flight. For a still-running step, UNION the
  // server rows with the live rows already present; the terminal force-refetch
  // is authoritative and replaces with the canonical persisted rows.
  const isTerminal = AiProcessStepTerminalStatuses.has(state.stepsById[stepExecutionId]?.status);
  const existingIds = state.messagesByStepId[stepExecutionId]?.ids ?? [];
  const ids = isTerminal
    ? serverIds
    : [...serverIds, ...existingIds.filter(id => !serverIds.includes(id))];

  return {
    ...state,
    messagesByStepId: {
      ...state.messagesByStepId,
      [stepExecutionId]: { ids, status: LoadStatus.READY },
    },
    messagesById: { ...state.messagesById, ...messagesRecord },
  };
};

const handleLoadStepMessagesFailure = (state, { stepExecutionId }) => ({
  ...state,
  messagesByStepId: {
    ...state.messagesByStepId,
    [stepExecutionId]: {
      ids: state.messagesByStepId[stepExecutionId]?.ids ?? [],
      status: LoadStatus.ERROR,
    },
  },
});

const handleLoadStepCitations = (state, { stepExecutionId }) => ({
  ...state,
  citationsByStepId: {
    ...state.citationsByStepId,
    [stepExecutionId]: {
      items: state.citationsByStepId[stepExecutionId]?.items ?? [],
      status: LoadStatus.LOADING,
    },
  },
});

const handleLoadStepCitationsSuccess = (state, { stepExecutionId, citations }) => ({
  ...state,
  citationsByStepId: {
    ...state.citationsByStepId,
    [stepExecutionId]: { items: citations, status: LoadStatus.READY },
  },
});

const handleLoadStepCitationsFailure = (state, { stepExecutionId }) => ({
  ...state,
  citationsByStepId: {
    ...state.citationsByStepId,
    [stepExecutionId]: {
      items: state.citationsByStepId[stepExecutionId]?.items ?? [],
      status: LoadStatus.ERROR,
    },
  },
});

const handleLoadRunCitations = (state, { executionId }) => ({
  ...state,
  citationsByExecutionId: {
    ...state.citationsByExecutionId,
    [executionId]: {
      items: state.citationsByExecutionId[executionId]?.items ?? [],
      status: LoadStatus.LOADING,
    },
  },
});

const handleLoadRunCitationsSuccess = (state, { executionId, citations }) => ({
  ...state,
  citationsByExecutionId: {
    ...state.citationsByExecutionId,
    [executionId]: { items: citations, status: LoadStatus.READY },
  },
});

const handleLoadRunCitationsFailure = (state, { executionId }) => ({
  ...state,
  citationsByExecutionId: {
    ...state.citationsByExecutionId,
    [executionId]: {
      items: state.citationsByExecutionId[executionId]?.items ?? [],
      status: LoadStatus.ERROR,
    },
  },
});

const handleLoadStepBlob = (state, { stepExecutionId, kind }) => {
  const existing = state.blobsByStepId[stepExecutionId] ?? {};

  return {
    ...state,
    blobsByStepId: {
      ...state.blobsByStepId,
      [stepExecutionId]: {
        ...existing,
        [kind]: { value: existing[kind]?.value ?? null, status: LoadStatus.LOADING },
      },
    },
  };
};

const handleLoadStepBlobSuccess = (state, { stepExecutionId, kind, blob }) => {
  const existing = state.blobsByStepId[stepExecutionId] ?? {};

  return {
    ...state,
    blobsByStepId: {
      ...state.blobsByStepId,
      [stepExecutionId]: {
        ...existing,
        [kind]: { value: blob, status: LoadStatus.READY },
      },
    },
  };
};

const handleLoadStepBlobFailure = (state, { stepExecutionId, kind }) => {
  const existing = state.blobsByStepId[stepExecutionId] ?? {};

  return {
    ...state,
    blobsByStepId: {
      ...state.blobsByStepId,
      [stepExecutionId]: {
        ...existing,
        [kind]: { value: existing[kind]?.value ?? null, status: LoadStatus.ERROR },
      },
    },
  };
};

// Opening a guideline citation's full-section drawer only needs to load content
// when it isn't already cached; the loading flag drives the drawer's spinner.
const handleSetStepGuidelineFullView = (state, { citation }) => ({
  ...state,
  guidelineFullView: citation,
  isLoadingGuideline: !_.isNil(citation) && _.isNil(state.guidelineCache[citation.id]),
});

const handleCacheStepGuidelineContent = (state, { id, content }) => ({
  ...state,
  guidelineCache: { ...state.guidelineCache, [id]: content },
  isLoadingGuideline: false,
});

const handleLoadStepGuidelineContentFailure = state => ({
  ...state,
  isLoadingGuideline: false,
});

const handleLoadStepPendingToolCallSuccess = (state, { stepExecutionId, pendingToolCall }) => ({
  ...state,
  pendingToolCallByStepId: {
    ...state.pendingToolCallByStepId,
    [stepExecutionId]: pendingToolCall ?? null,
  },
});

const handleLoadStepPendingToolCallFailure = (state, { stepExecutionId }) => ({
  ...state,
  pendingToolCallByStepId: {
    ...state.pendingToolCallByStepId,
    [stepExecutionId]: null,
  },
});

const handleSetStepPendingToolCall = (state, { stepExecutionId, pendingToolCall }) => {
  if (!stepExecutionId || !pendingToolCall) {
    return state;
  }

  return {
    ...state,
    pendingToolCallByStepId: {
      ...state.pendingToolCallByStepId,
      [stepExecutionId]: pendingToolCall,
    },
  };
};

const handleLoadStepPendingQuestionSuccess = (state, { stepExecutionId, pendingQuestion }) => ({
  ...state,
  pendingQuestionByStepId: {
    ...state.pendingQuestionByStepId,
    [stepExecutionId]: pendingQuestion ?? null,
  },
});

const handleLoadStepPendingQuestionFailure = (state, { stepExecutionId }) => ({
  ...state,
  pendingQuestionByStepId: {
    ...state.pendingQuestionByStepId,
    [stepExecutionId]: null,
  },
});

const handleSetStepPendingQuestion = (state, { stepExecutionId, pendingQuestion }) => {
  if (!stepExecutionId || !pendingQuestion) {
    return state;
  }

  return {
    ...state,
    pendingQuestionByStepId: {
      ...state.pendingQuestionByStepId,
      [stepExecutionId]: pendingQuestion,
    },
  };
};

const handleAnswerStepQuestion = (state, { stepExecutionId }) => ({
  ...state,
  answeringStepIds: { ...state.answeringStepIds, [stepExecutionId]: true },
});

const handleAnswerStepQuestionSettled = (state, { stepExecutionId }) => {
  const next = { ...state.answeringStepIds };
  delete next[stepExecutionId];

  return { ...state, answeringStepIds: next };
};

const handleSelectExecution = (state, { ownerId, executionId }) => ({
  ...state,
  selectedExecutionIdByOwner: {
    ...state.selectedExecutionIdByOwner,
    [ownerId]: executionId,
  },
});

const handleExecutionUpsert = (state, { execution, ownerId }) => {
  if (!execution || !execution.id) {
    return state;
  }

  const { runsByOwner, runsById } = upsertExecution(state, execution, ownerId);

  return { ...state, runsByOwner, runsById };
};

const handleStepUpsert = (state, { step }) => {
  if (!step || !step.id) {
    return state;
  }

  const { stepsByRunId, stepsById } = upsertStep(state, step);

  return { ...state, stepsByRunId, stepsById };
};

const handleMessageAppend = (state, { stepExecutionId, message }) => {
  if (!stepExecutionId || !message || !message.id) {
    return state;
  }

  const { messagesByStepId, messagesById } = appendMessageToStep(state, stepExecutionId, message);

  return { ...state, messagesByStepId, messagesById };
};

const handleConfirmStepExecution = (state, { stepExecutionId }) => ({
  ...state,
  confirmingStepIds: { ...state.confirmingStepIds, [stepExecutionId]: true },
});

const handleConfirmStepExecutionSettled = (state, { stepExecutionId }) => {
  const next = { ...state.confirmingStepIds };
  delete next[stepExecutionId];

  return { ...state, confirmingStepIds: next };
};

const handleRetryStepExecution = (state, { stepExecutionId }) => ({
  ...state,
  retryingStepIds: { ...state.retryingStepIds, [stepExecutionId]: true },
});

const handleRetryStepExecutionSettled = (state, { stepExecutionId }) => {
  const next = { ...state.retryingStepIds };
  delete next[stepExecutionId];

  return { ...state, retryingStepIds: next };
};

const handleRetryStepFromHereExecution = (state, { stepExecutionId }) => ({
  ...state,
  retryingFromHereStepIds: { ...state.retryingFromHereStepIds, [stepExecutionId]: true },
});

const handleRetryStepFromHereExecutionSettled = (state, { stepExecutionId }) => {
  const next = { ...state.retryingFromHereStepIds };
  delete next[stepExecutionId];

  return { ...state, retryingFromHereStepIds: next };
};

const handleLoadForm = (state, { executionId }) => ({
  ...state,
  formLoadStatusByExecutionId: {
    ...state.formLoadStatusByExecutionId,
    [executionId]: LoadStatus.LOADING,
  },
});

// Dual-source: a live form_field_set/form_status_changed may have already
// seeded the form before this REST resolve lands. The live payload wins, so a
// REST reload only fills the slot when it's still empty. A forced reload (the
// SignalR-reconnect reconcile) is the exception: live state may have drifted
// while the socket was down, so the authoritative REST snapshot replaces it.
const handleLoadFormSuccess = (state, { executionId, force, form }) => {
  const existing = state.formByExecutionId[executionId];
  const next = force ? (form ?? existing ?? null) : (existing ?? form ?? null);

  return {
    ...state,
    formByExecutionId: {
      ...state.formByExecutionId,
      [executionId]: next,
    },
    formLoadStatusByExecutionId: {
      ...state.formLoadStatusByExecutionId,
      [executionId]: LoadStatus.READY,
    },
  };
};

const handleLoadFormFailure = (state, { executionId }) => ({
  ...state,
  formLoadStatusByExecutionId: {
    ...state.formLoadStatusByExecutionId,
    [executionId]: LoadStatus.ERROR,
  },
});

const handleFormUpsert = (state, { form }) => {
  if (!form?.executionId) {
    return state;
  }

  return {
    ...state,
    formByExecutionId: {
      ...state.formByExecutionId,
      [form.executionId]: form,
    },
  };
};

const handleFormFieldSet = (state, { executionId, event }) => {
  const existing = state.formByExecutionId[executionId];

  if (!existing) {
    return state;
  }

  return {
    ...state,
    formByExecutionId: {
      ...state.formByExecutionId,
      [executionId]: mergeFormFieldEvent(existing, event),
    },
  };
};

const handleFormStatusChanged = (state, { executionId, status }) => {
  const existing = state.formByExecutionId[executionId];

  if (!existing || !status) {
    return state;
  }

  return {
    ...state,
    formByExecutionId: {
      ...state.formByExecutionId,
      [executionId]: { ...existing, status },
    },
  };
};

const handleFormActionInFlight = (state, { formId }) => ({
  ...state,
  formActionInFlightById: { ...state.formActionInFlightById, [formId]: true },
});

const handleFormActionSettled = (state, { formId }) => {
  const next = { ...state.formActionInFlightById };
  delete next[formId];

  return { ...state, formActionInFlightById: next };
};

const handlersByType = {
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUNS]: handleLoadRuns,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUNS_SUCCESS]: handleLoadRunsSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUNS_FAILURE]: handleLoadRunsFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.SET_RUNS_FILTER]: handleSetRunsFilter,
  [AI_PROCESS_RUNS_ACTION_TYPE.SET_RUNS_PAGE]: handleSetRunsPage,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_PROCESSES_USED]: handleLoadProcessesUsed,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_PROCESSES_USED_SUCCESS]: handleLoadProcessesUsedSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_PROCESSES_USED_FAILURE]: handleLoadProcessesUsedFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_DETAIL]: handleLoadRunDetail,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_DETAIL_SUCCESS]: handleLoadRunDetailSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_DETAIL_FAILURE]: handleLoadRunDetailFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_MESSAGES]: handleLoadStepMessages,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_MESSAGES_SUCCESS]: handleLoadStepMessagesSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_MESSAGES_FAILURE]: handleLoadStepMessagesFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_CITATIONS]: handleLoadStepCitations,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_CITATIONS_SUCCESS]: handleLoadStepCitationsSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_CITATIONS_FAILURE]: handleLoadStepCitationsFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_CITATIONS]: handleLoadRunCitations,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_CITATIONS_SUCCESS]: handleLoadRunCitationsSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_CITATIONS_FAILURE]: handleLoadRunCitationsFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_BLOB]: handleLoadStepBlob,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_BLOB_SUCCESS]: handleLoadStepBlobSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_BLOB_FAILURE]: handleLoadStepBlobFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.SET_STEP_GUIDELINE_FULL_VIEW]: handleSetStepGuidelineFullView,
  [AI_PROCESS_RUNS_ACTION_TYPE.CACHE_STEP_GUIDELINE_CONTENT]: handleCacheStepGuidelineContent,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_GUIDELINE_CONTENT_FAILURE]:
    handleLoadStepGuidelineContentFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_PENDING_TOOL_CALL_SUCCESS]:
    handleLoadStepPendingToolCallSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_PENDING_TOOL_CALL_FAILURE]:
    handleLoadStepPendingToolCallFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.SET_STEP_PENDING_TOOL_CALL]: handleSetStepPendingToolCall,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_PENDING_QUESTION_SUCCESS]:
    handleLoadStepPendingQuestionSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_PENDING_QUESTION_FAILURE]:
    handleLoadStepPendingQuestionFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.SET_STEP_PENDING_QUESTION]: handleSetStepPendingQuestion,
  [AI_PROCESS_RUNS_ACTION_TYPE.ANSWER_STEP_QUESTION]: handleAnswerStepQuestion,
  [AI_PROCESS_RUNS_ACTION_TYPE.ANSWER_STEP_QUESTION_SUCCESS]: handleAnswerStepQuestionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.ANSWER_STEP_QUESTION_FAILURE]: handleAnswerStepQuestionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.SELECT_EXECUTION]: handleSelectExecution,
  [AI_PROCESS_RUNS_ACTION_TYPE.EXECUTION_UPSERT]: handleExecutionUpsert,
  [AI_PROCESS_RUNS_ACTION_TYPE.STEP_UPSERT]: handleStepUpsert,
  [AI_PROCESS_RUNS_ACTION_TYPE.MESSAGE_APPEND]: handleMessageAppend,
  [AI_PROCESS_RUNS_ACTION_TYPE.CONFIRM_STEP_EXECUTION]: handleConfirmStepExecution,
  [AI_PROCESS_RUNS_ACTION_TYPE.CONFIRM_STEP_EXECUTION_SUCCESS]: handleConfirmStepExecutionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.CONFIRM_STEP_EXECUTION_FAILURE]: handleConfirmStepExecutionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_EXECUTION]: handleRetryStepExecution,
  [AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_EXECUTION_SUCCESS]: handleRetryStepExecutionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_EXECUTION_FAILURE]: handleRetryStepExecutionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_FROM_HERE_EXECUTION]: handleRetryStepFromHereExecution,
  [AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_FROM_HERE_EXECUTION_SUCCESS]:
    handleRetryStepFromHereExecutionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_FROM_HERE_EXECUTION_FAILURE]:
    handleRetryStepFromHereExecutionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_FORM]: handleLoadForm,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_FORM_SUCCESS]: handleLoadFormSuccess,
  [AI_PROCESS_RUNS_ACTION_TYPE.LOAD_FORM_FAILURE]: handleLoadFormFailure,
  [AI_PROCESS_RUNS_ACTION_TYPE.FORM_UPSERT]: handleFormUpsert,
  [AI_PROCESS_RUNS_ACTION_TYPE.FORM_FIELD_SET]: handleFormFieldSet,
  [AI_PROCESS_RUNS_ACTION_TYPE.FORM_STATUS_CHANGED]: handleFormStatusChanged,
  [AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELD]: handleFormActionInFlight,
  [AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELD_SUCCESS]: handleFormActionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELD_FAILURE]: handleFormActionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELDS]: handleFormActionInFlight,
  [AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELDS_SUCCESS]: handleFormActionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELDS_FAILURE]: handleFormActionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.TRANSITION_FORM]: handleFormActionInFlight,
  [AI_PROCESS_RUNS_ACTION_TYPE.TRANSITION_FORM_SUCCESS]: handleFormActionSettled,
  [AI_PROCESS_RUNS_ACTION_TYPE.TRANSITION_FORM_FAILURE]: handleFormActionSettled,
};

export const aiProcessRunsReducer = (state = initialState, action = {}) => {
  const handler = _.get(handlersByType, action.type);

  if (!handler) {
    return state;
  }

  return handler(state, action.payload);
};
